import React from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import type { ImmutableEntity } from '@uncinc/react-drupal-core/types';
import {
  Container,
  Icon,
  Image,
  WysiwygField,
  useWindowDimensions,
} from '@uncinc/uncinc-react-kitchen-sink';

import { NodeArticle } from '../../../../types/uncinc_profile/nodes';
import {
  ArticleMetadata,
  Breadcrumbs,
  ParagraphsRenderer,
  MoreLikeThis,
} from '../../../../components';
import {
  SCREEN_NORMAL, DEFAULT_LANGUAGE,
} from '../../../../config/constants';

import './style.scss';

interface NodeArticlePageProps extends React.ComponentPropsWithoutRef<'div'> {
  entity: ImmutableEntity<NodeArticle>;
}

const NodeArticlePage: React.FC<NodeArticlePageProps> = ({ entity }) => {
  const { t } = useTranslation();
  const classes = classNames('Node Node-type-breadcrumbs NodeArticlePage');
  const article = entity.toJS();
  const author = article.field_author[0];
  const title = article.title[0].value;
  const publicationDate = article.field_publish_date?.[0]?.value || article.created[0]?.value;
  const paragraphs = article.field_paragraphs;
  const labels = article.field_labels;
  const themes = article.field_themes;
  const readTime = article.node_read_time[0]?.value;

  const body = article.body[0]?.processed;
  const lang = article.langcode[0]?.value || DEFAULT_LANGUAGE;
  const nid = article.nid[0]?.value;
  const imageStyles = article.field_media_item[0]?.styles;

  const { width } = useWindowDimensions();
  const isMobile = width < SCREEN_NORMAL;

  let ratio: string = '1_1';
  if (isMobile) {
    ratio = '3_4';
  }

  const src = ['xs', 'sm', 'md', 'xl'].map((sizes) => {
    const style = `ratio_${ratio}_${sizes}`;
    return `${imageStyles?.[style] || ''}`;
  });

  return (
    <article itemType="http://schema.org/WebPage" className={classes}>
      <div className="NodeArticlePage__hero">
        <Container>
          <div className="row">
            <div className="col-12 col-md-10 offset-md-1 offset-lg-0">
              <Breadcrumbs
                parentTitle={t('content_types.article.overview')}
                parentLink={`/${lang}/${t('content_types.article.overview').toLowerCase()}`}
                currentPage={title}
              />
            </div>
          </div>
          <div className="NodeArticlePage__hero__content row">
            {imageStyles && (
              <div className="col-12 col-md-10 offset-md-1 col-lg-5 offset-lg-0 order-3 order-lg-0 extra-class-margin-bottom">
                <div className="NodeArticlePage__hero__image">
                  <Image
                    src={src}
                    alt={title}
                  />
                </div>
              </div>
            )}
            <div className="NodeArticlePage__hero__text col-12 col-md-10 offset-md-1 col-lg-6">

              <ArticleMetadata>
                <div className="NodeArticlePage metadata text--regular">
                  <div className="publicationDate">
                    <Icon name="today" />
                    <ArticleMetadata.PublicationDate publicationDate={new Date(publicationDate)} />
                  </div>
                  {readTime && (
                    <div className="readtime">
                      <Icon name="access_time" />
                      <ArticleMetadata.ReadTime readTime={Number(readTime)} />
                    </div>
                  )}
                </div>
              </ArticleMetadata>

              <h1 itemProp="name">{title}</h1>
              {body && (
                <WysiwygField
                  property="schema:text"
                  html={body}
                />
              )}
            </div>
          </div>
        </Container>
      </div>

      <ParagraphsRenderer paragraphs={paragraphs} />

      {/* <Container>
        {author && (
          <div className="NodeArticlePage__author col-12 col-md-5 offset-md-1 col-lg-4 offset-lg-2">
            <span>toodo author toevoegen</span> https://uncinc.monday.com/boards/456111162/pulses/6453550226
          </div>
        )}
      </Container> */}

      <MoreLikeThis
        labels={labels}
        themes={themes}
        entity={article}
      />

    </article>
  );
};

export default NodeArticlePage;
